import { AppError, ErrorEvent } from '../types/error';
import { ErrorFactory } from './errorFactory';

/**
 * Error logging and monitoring service
 */
export class ErrorLogger {
  private static errorQueue: ErrorEvent[] = [];
  private static maxQueueSize = 100;
  private static flushInterval = 30000; // 30 seconds
  private static isInitialized = false;
  private static isLoggingError = false; // Prevent recursive error logging
  private static recentErrors = new Set<string>(); // Track recent error signatures
  private static errorSignatureExpiry = 5000; // 5 seconds

  static initialize() {
    if (this.isInitialized) return;

    // Start periodic flush
    setInterval(() => {
      this.flush();
    }, this.flushInterval);

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      this.flush();
    });

    this.isInitialized = true;
  }

  /**
   * Log an error to the queue and potentially external services
   */
  static async logError(
    error: AppError,
    additionalContext?: Record<string, any>
  ): Promise<void> {
    // Prevent recursive error logging
    if (this.isLoggingError) {
      console.warn('Prevented recursive error logging');
      return;
    }

    // Generate error signature for deduplication
    const errorSignature = `${error.code}-${error.category}-${error.technicalMessage?.substring(0, 50)}`;

    // Skip if we've recently logged the same error
    if (this.recentErrors.has(errorSignature)) {
      return;
    }

    try {
      this.isLoggingError = true;
      this.recentErrors.add(errorSignature);

      // Clear signature after expiry time
      setTimeout(() => {
        this.recentErrors.delete(errorSignature);
      }, this.errorSignatureExpiry);

      const errorEvent: ErrorEvent = {
        error: {
          ...error,
          context: {
            ...error.context,
            ...additionalContext
          }
        },
        timestamp: new Date(),
        handled: true
      };

      // Add to queue
      this.addToQueue(errorEvent);

      // Log to console in development (skip repetitive image load errors)
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG) {
        // Skip logging repetitive image loading errors
        const isImageLoadError = error.code === 'RESOURCE_LOAD_ERROR' &&
          error.technicalMessage?.includes('Failed to load img');

        if (!isImageLoadError) {
          console.group(`🚨 ${error.category.toUpperCase()} Error: ${error.code}`);
          console.error('User Message:', error.userMessage);
          console.error('Technical Message:', error.technicalMessage);
          console.error('Context:', error.context);
          if (error.originalError) {
            console.error('Original Error:', error.originalError);
          }
          console.groupEnd();
        }
      }

      // Send critical errors immediately
      if (error.severity === 'critical') {
        await this.sendErrorToExternalService(errorEvent);
      }
    } catch (loggingError) {
      // If error logging itself fails, just log to console without recursion
      console.error('Failed to log error:', loggingError);
    } finally {
      this.isLoggingError = false;
    }
  }

  /**
   * Log user action for error resolution tracking
   */
  static logUserAction(
    errorCode: string,
    action: string,
    resolution?: string
  ): void {
    const event = this.errorQueue.find(e => e.error.code === errorCode);
    if (event) {
      event.userAction = action;
      event.resolution = resolution;
      if (resolution) {
        event.resolutionTime = Date.now() - event.timestamp.getTime();
      }
    }
  }

  /**
   * Add error to the queue
   */
  private static addToQueue(errorEvent: ErrorEvent): void {
    this.errorQueue.push(errorEvent);

    // Maintain queue size
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }
  }

  /**
   * Flush error queue to external services
   */
  private static async flush(): Promise<void> {
    if (this.errorQueue.length === 0) return;

    const errorsToSend = [...this.errorQueue];
    this.errorQueue = [];

    try {
      await this.sendErrorsToExternalService(errorsToSend);
    } catch (error) {
      console.warn('Failed to send errors to external service:', error);
      // Re-add errors to queue if sending failed
      this.errorQueue.unshift(...errorsToSend.slice(-10)); // Keep last 10
    }
  }

  /**
   * Send multiple errors to external monitoring service
   */
  private static async sendErrorsToExternalService(
    errors: ErrorEvent[]
  ): Promise<void> {
    // In production, this would send to services like Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_ERROR_REPORTING_URL) {
      try {
        await fetch(process.env.REACT_APP_ERROR_REPORTING_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            errors: errors.map(this.sanitizeErrorForSending),
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href
          })
        });
      } catch (error) {
        console.warn('Failed to send errors to monitoring service:', error);
      }
    }

    // Note: Removed error re-throwing to prevent recursive loops
  }

  /**
   * Send single error immediately (for critical errors)
   */
  private static async sendErrorToExternalService(
    errorEvent: ErrorEvent
  ): Promise<void> {
    await this.sendErrorsToExternalService([errorEvent]);
  }

  /**
   * Sanitize error data before sending to external services
   */
  private static sanitizeErrorForSending(errorEvent: ErrorEvent): any {
    const { error } = errorEvent;

    return {
      code: error.code,
      category: error.category,
      severity: error.severity,
      userMessage: error.userMessage,
      technicalMessage: error.technicalMessage,
      recoverable: error.recoverable,
      retryable: error.retryable,
      timestamp: errorEvent.timestamp,
      handled: errorEvent.handled,
      userAction: errorEvent.userAction,
      resolution: errorEvent.resolution,
      resolutionTime: errorEvent.resolutionTime,
      context: {
        ...error.context,
        // Remove sensitive data
        userId: error.context?.userId ? '***' : undefined,
        sessionId: error.context?.sessionId ? '***' : undefined,
      },
      // Include stack trace only in development
      stack: process.env.NODE_ENV === 'development'
        ? error.originalError?.stack
        : undefined
    };
  }

  /**
   * Get error statistics for analytics
   */
  static getErrorStats(): {
    totalErrors: number;
    errorsByCategory: Record<string, number>;
    errorsBySeverity: Record<string, number>;
    resolvedErrors: number;
    averageResolutionTime: number;
  } {
    const stats = {
      totalErrors: this.errorQueue.length,
      errorsByCategory: {} as Record<string, number>,
      errorsBySeverity: {} as Record<string, number>,
      resolvedErrors: 0,
      averageResolutionTime: 0
    };

    let totalResolutionTime = 0;

    this.errorQueue.forEach(event => {
      const { error } = event;

      // Count by category
      stats.errorsByCategory[error.category] =
        (stats.errorsByCategory[error.category] || 0) + 1;

      // Count by severity
      stats.errorsBySeverity[error.severity] =
        (stats.errorsBySeverity[error.severity] || 0) + 1;

      // Count resolved errors
      if (event.resolution) {
        stats.resolvedErrors++;
        totalResolutionTime += event.resolutionTime || 0;
      }
    });

    // Calculate average resolution time
    if (stats.resolvedErrors > 0) {
      stats.averageResolutionTime = totalResolutionTime / stats.resolvedErrors;
    }

    return stats;
  }

  /**
   * Clear error queue (useful for testing)
   */
  static clearQueue(): void {
    this.errorQueue = [];
  }

  /**
   * Get recent errors for debugging
   */
  static getRecentErrors(limit: number = 10): ErrorEvent[] {
    return this.errorQueue.slice(-limit);
  }

  /**
   * Set up global error handlers
   */
  static setupGlobalErrorHandlers(): void {
    // Prevent setting up handlers multiple times
    if (this.isInitialized) return;

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error = this.createErrorFromRejection(event.reason);
      this.logError(error);
      event.preventDefault(); // Prevent default browser error display
    });

    // Handle uncaught JavaScript errors
    window.addEventListener('error', (event) => {
      // Only handle JavaScript errors, not resource loading errors
      if (event.target === window || event.target === document) {
        const error = this.createErrorFromJavaScriptError(event);
        this.logError(error);
        event.preventDefault(); // Prevent default browser error display
      }
    });

    // Handle resource loading errors separately
    window.addEventListener('error', (event) => {
      if (event.target !== window && event.target !== document) {
        const error = this.createErrorFromResourceError(event);
        this.logError(error);
      }
    }, true);
  }

  private static createErrorFromRejection(reason: any): AppError {
    return ErrorFactory.fromUnknown(
      reason,
      'UNHANDLED_PROMISE_REJECTION',
      'An unexpected error occurred in the application'
    );
  }

  private static createErrorFromJavaScriptError(event: globalThis.ErrorEvent): AppError {
    return ErrorFactory.fromUnknown(
      new Error(event.message),
      'UNCAUGHT_JAVASCRIPT_ERROR',
      'A JavaScript error occurred',
      {
        url: event.filename,
        additionalData: {
          line: event.lineno,
          column: event.colno
        }
      }
    );
  }

  private static createErrorFromResourceError(event: Event): AppError {
    const target = event.target as HTMLElement;
    const resourceType = target.tagName?.toLowerCase();
    const resourceUrl = (target as any).src || (target as any).href;

    return ErrorFactory.createNetworkError(
      'RESOURCE_LOAD_ERROR',
      'Failed to load a required resource',
      `Failed to load ${resourceType}: ${resourceUrl}`,
      undefined,
      resourceUrl,
      'GET'
    );
  }
}

// Initialize error logger
ErrorLogger.initialize();
ErrorLogger.setupGlobalErrorHandlers();
